import { Badge, Card } from '../../../ui';
import { fmtWhen, templateOf, type PageRow } from './format';

const faint: React.CSSProperties = { color: 'var(--text-faint)' };
const pct = (n: number | null | undefined) => (n == null ? '—' : `${Math.round(n * 100)}%`);

/**
 * Page detail → Templated content: how much of this page is also on its closest siblings (the screen in
 * scripts/lib/sameness.mjs). The fix is queued from the journal below.
 */
export default function TemplateCard({ page }: { page: PageRow }) {
  const t = templateOf(page);
  const templated = t?.level === 'templated';
  const note = (text: React.ReactNode) => <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{text}</div>;

  return (
    <Card
      eyebrow="Templated content"
      title={!t ? 'Not checked yet' : t.share == null ? 'Too short to judge' : templated ? `${pct(t.share)} the same as a sibling` : `Own page · ${pct(t.share)} at most`}
      actions={templated ? <Badge tone="down">templated</Badge> : null}
    >
      {!t ? (
        note('The check runs with the next pages sync.')
      ) : t.siblings.length === 0 ? (
        note(t.share == null ? 'Under about 110 words of body text, so there is nothing to compare.' : 'No other page shares more than 20% of this one.')
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: 'var(--text-sm)' }}>
          {t.siblings.map((s) => (
            <div key={s.url} style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
              <span className="t-num" style={{ width: '44px', textAlign: 'right', color: s.share >= 0.5 ? 'var(--status-down)' : 'var(--text-body)' }}>{pct(s.share)}</span>
              <a href={s.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent-text)', overflowWrap: 'anywhere' }}>
                {s.url}
              </a>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)', ...faint }}>
        Share of this page’s text (8-word runs, place names masked) also on that page. Templated = 50%+ on one sibling; a rewrite should land under 20%.
        {page.template_checked_at ? ` · checked ${fmtWhen(page.template_checked_at)}` : ''}
      </div>
    </Card>
  );
}
